import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, ArrowRight, ShieldCheck, Star, Clock, MapPin, Check } from "lucide-react";
import { SERVICES, type ServiceContent } from "../data/services";
import { openQuoteModal } from "../components/quote-modal";
import { Reveal } from "../components/reveal";
import { Breadcrumbs } from "../components/breadcrumbs";
import { HomeReviews } from "../components/home-reviews";
import { SHOW_LICENSED_INSURED } from "../data/flags";
import { InternalLinkRow, CORE_CITY_LINKS } from "@/components/internal-links";
import { InlineQuoteSection } from "@/components/inline-quote-section";

const SITE = "https://capitalpropressurewashing.com";
const TITLE = "Pressure Washing Services | Albany & Capital Region";
const DESC =
  "Full-service exterior cleaning in Albany NY and the Capital Region. House washing, roof washing, gutter cleaning, concrete sealing, pavers, decks, windows, commercial washing and more.";

const COMMERCIAL_SLUGS = new Set([
  "agricultural-barn-washing",
  "commercial-fleet-washing",
  "commercial-heavy-equipment-cleaning",
  "commercial-graffiti-removal",
  "commercial-gum-removal",
  "commercial-awning-cleaning",
  "commercial-building-washing",
  "commercial-dumpster-pad-cleaning",
  "commercial-property-washing",
]);

function isCommercial(s: ServiceContent) {
  return COMMERCIAL_SLUGS.has(s.slug);
}

const residential = SERVICES.filter((s) => !isCommercial(s));
const commercial = SERVICES.filter(isCommercial);

export const Route = createFileRoute("/services")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}/services` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: `${SITE}/services` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "Services", item: `${SITE}/services` },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Pressure Washing and Exterior Cleaning",
          provider: {
            "@type": "LocalBusiness",
            name: "Capital Pro Pressure Washing",
            url: SITE,
            telephone: "+15189001913",
            areaServed: "Capital Region, NY",
          },
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Exterior Cleaning Services",
            itemListElement: SERVICES.map((s) => ({
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: s.name,
                url: `${SITE}/${s.slug}`,
              },
            })),
          },
        }),
      },
    ],
  }),
  component: ServicesPage,
});

function ServiceCard({ service, index }: { service: ServiceContent; index: number }) {
  const Icon = service.icon;
  return (
    <Reveal delayMs={index * 60}>
      <Link
        to="/$service"
        params={{ service: service.slug }}
        className="group card-pop flex h-full flex-col rounded-lg border border-gray-200 bg-white p-5 transition-colors hover:border-[var(--brand-deep-blue)]/30"
      >
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-md bg-[var(--brand-deep-blue)]/10 text-[var(--brand-deep-blue)] transition-colors group-hover:bg-[var(--brand-red)] group-hover:text-white">
          <Icon size={24} />
        </div>
        <h3
          className="text-[var(--brand-deep-blue)] text-lg uppercase leading-tight"
          style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
        >
          {service.name}
        </h3>
        <p className="mt-2 flex-1 text-sm text-[var(--brand-charcoal)]/80 leading-relaxed">
          {service.intro}
        </p>
        <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[var(--brand-red)] uppercase tracking-wide">
          Learn more <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
        </span>
      </Link>
    </Reveal>
  );
}

function ServiceGroup({ title, subtitle, services }: { title: string; subtitle: string; services: ServiceContent[] }) {
  return (
    <section className="py-12 md:py-16">
      <div className="mx-auto max-w-[1200px] px-4">
        <div className="mb-8 md:mb-10 text-center">
          <p
            className="mb-2 text-sm font-semibold uppercase tracking-[0.2em] text-[var(--brand-red)]"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {subtitle}
          </p>
          <h2
            className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            {title}
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s, i) => (
            <ServiceCard key={s.slug} service={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesPage() {
  return (
    <main>
      <Breadcrumbs items={[{ label: "Services" }]} />

      {/* Hero */}
      <section className="bg-[var(--brand-deep-blue)] text-white py-14 md:py-20">
        <div className="mx-auto max-w-[1200px] px-4 text-center">
          <p
            className="font-semibold uppercase tracking-[0.2em] text-sm mb-3"
            style={{ fontFamily: "var(--font-display)", color: "#ff6b7f" }}
          >
            Capital Region, NY
          </p>
          <h1
            className="text-3xl md:text-5xl uppercase leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Pressure Washing Services in Albany & the Capital Region
          </h1>
          <p className="mt-4 text-white/85 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            One crew, every exterior surface. From house washing and roof moss treatment to concrete sealing, gutter guards, and commercial washing — we clean homes and businesses across Albany, Schenectady, Troy, Saratoga Springs, and every town in between.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => openQuoteModal()}
              className="inline-flex items-center justify-center rounded-md bg-[#C8102E] text-white px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Get Free Quote
            </button>
            <a
              href="tel:+15189001913"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-white text-[var(--brand-deep-blue)] px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              <Phone size={18} strokeWidth={2.5} />
              Call (518) 900-1913
            </a>
          </div>
        </div>
      </section>

      {/* Intro / trust */}
      <section className="bg-white py-12 md:py-16 border-b border-gray-100">
        <div className="mx-auto max-w-[900px] px-4 text-center">
          <h2
            className="text-[var(--brand-deep-blue)] text-2xl md:text-3xl uppercase leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            What We Clean
          </h2>
          <p className="mt-4 text-[var(--brand-charcoal)]/85 text-base md:text-lg leading-relaxed">
            Not every surface should be hit with the same pressure. We match the method to the material — soft washing for siding and roofs, pressure washing for concrete and pavers, and specialized detergents for rust, stains, and organic buildup. Every quote is free, every job is documented, and the same crew that shows up for the estimate does the work.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4 text-sm text-[var(--brand-charcoal)]/80">
            <span className="inline-flex items-center gap-2">
              <Check size={16} className="text-[#C8102E]" /> Free written estimates
            </span>
            <span className="inline-flex items-center gap-2">
              <Check size={16} className="text-[#C8102E]" /> Photo documentation
            </span>
            <span className="inline-flex items-center gap-2">
              <Check size={16} className="text-[#C8102E]" /> Before/after photos included
            </span>
            {SHOW_LICENSED_INSURED && (
              <span className="inline-flex items-center gap-2">
                <Check size={16} className="text-[#C8102E]" /> Insured & background-checked crew
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Residential services */}
      <ServiceGroup
        title="Residential Pressure Washing"
        subtitle="Homes, Roofs, Driveways & More"
        services={residential}
      />

      {/* Commercial services */}
      <div className="bg-[var(--brand-light-gray)]">
        <ServiceGroup
          title="Commercial Pressure Washing"
          subtitle="Buildings, Fleets, Barns & Property Maintenance"
          services={commercial}
        />
      </div>

      {/* Trust strip */}
      <section className="bg-white py-10 md:py-12">
        <div className="mx-auto max-w-[1000px] px-4">
          <Reveal className="rounded-lg bg-[var(--brand-light-gray)] border border-gray-200 p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-center md:text-left">
              <div className="flex items-center gap-2">
                <div className="flex">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={20} className="text-[#fbbf24]" fill="#fbbf24" />
                  ))}
                </div>
                <span className="text-[var(--brand-charcoal)] font-semibold text-sm">90+ Google Reviews</span>
              </div>
              <div className="hidden md:block w-px h-8 bg-gray-200" />
              {SHOW_LICENSED_INSURED && (
                <div className="inline-flex items-center gap-2 text-[var(--brand-charcoal)] font-semibold text-sm">
                  <ShieldCheck size={20} className="text-[var(--brand-deep-blue)]" />
                  Licensed & Insured
                </div>
              )}
              <div className="hidden md:block w-px h-8 bg-gray-200" />
              <div className="inline-flex items-center gap-2 text-[var(--brand-charcoal)] font-semibold text-sm">
                <Clock size={20} className="text-[var(--brand-deep-blue)]" />
                10+ Years Local
              </div>
              <div className="hidden md:block w-px h-8 bg-gray-200" />
              <div className="inline-flex items-center gap-2 text-[var(--brand-charcoal)] font-semibold text-sm">
                <MapPin size={20} className="text-[var(--brand-deep-blue)]" />
                Capital Region, NY
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Reviews */}
      <HomeReviews />

      {/* Final CTA */}
      <section className="bg-[var(--brand-deep-blue)] text-white py-12">
        <div className="mx-auto max-w-[1200px] px-4 text-center">
          <h2
            className="text-2xl md:text-3xl uppercase leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Find the Right Service for Your Property
          </h2>
          <p className="mt-2 text-white/85">
            Not sure which service you need? Tell us what you are trying to clean — we will match it to the right method and give you a free quote.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => openQuoteModal()}
              className="inline-flex items-center justify-center rounded-md bg-[#C8102E] text-white px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Get Free Quote
            </button>
            <a
              href="tel:+15189001913"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-white text-[var(--brand-deep-blue)] px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              <Phone size={18} strokeWidth={2.5} />
              Call (518) 900-1913
            </a>
          </div>
        </div>
      </section>
      <InternalLinkRow
        tone="white"
        eyebrow="Where We Work"
        title="Areas We Serve"
        links={CORE_CITY_LINKS}
      />

      <InlineQuoteSection source="Services Hub" />
    </main>
  );
}
